import { DateTime, Settings } from "luxon";

import { formatNumber, formatPercent } from "../../../utilities/format";
import i18n from "../../../i18n";
import { HourlyMetricsCardEntryDTO } from "../../../DTOs/Dashboards/HourlyMetricsCardEntryDTO";
import { PlantTypeDTO } from "../../../DTOs/PlantTypeDTO";
import { LinkTarget } from "../../../utilities/helpers/linkTarget";
import {
  OPERATIONS_TARGET,
  PRODUCTION_LIST_TARGET,
  STOPPAGES_TARGET,
} from "../../../utilities/helpers/dashboardLinks";

export interface HourlyMetricsChartPoint extends Omit<HourlyMetricsCardEntryDTO, "utilization" | "availability"> {
  displayHour: string;
  utilization: number | null;
  availability: number | null;
}

export interface HourlyMetricsChartData {
  points: HourlyMetricsChartPoint[];
  liveEdgeIndex: number;
}

const MILLIS_PER_HOUR = 3_600_000;

/**
 * Index of the current hour, -1 when the reference time is before the shift starts.
 * Clamped to the last slot so a shift that has run past its window stays fully drawn.
 */
export const resolveElapsedHourIndex = (
  shiftStartMillis: number,
  referenceMillis: number,
  hourCount: number
): number => {
  if (hourCount <= 0) return -1;

  const elapsed = Math.floor((referenceMillis - shiftStartMillis) / MILLIS_PER_HOUR);
  if (elapsed < 0) return -1;

  return Math.min(elapsed, hourCount - 1);
};

/**
 * The backend returns a row for every hour of the shift, and hours that have not happened yet
 * come back as zeros rather than nulls. Blanking the actuals past the hour in progress stops the
 * Utilization and Availability lines at the live edge instead of dragging them down to 0%.
 * Only the shift in progress is trimmed; a completed shift is all actuals.
 */
export const buildHourlyMetricsChartData = (
  metrics: HourlyMetricsCardEntryDTO[] | null,
  referenceMillis: number,
  isCurrentShift: boolean
): HourlyMetricsChartData => {
  if (!metrics?.length) return { points: [], liveEdgeIndex: -1 };

  const shiftStart = metrics[0].shiftStartUtc.setZone(Settings.defaultZone).startOf("hour");
  const liveEdgeIndex = isCurrentShift
    ? resolveElapsedHourIndex(shiftStart.toMillis(), referenceMillis, metrics.length)
    : metrics.length - 1;

  const points = metrics.map((entry, i) => ({
    ...entry,
    displayHour: shiftStart.plus({ hours: i }).toFormat("HH:00"),
    utilization: i > liveEdgeIndex ? null : entry.utilization,
    availability: i > liveEdgeIndex ? null : entry.availability,
  }));

  return { points, liveEdgeIndex };
};

/**
 * The type running the most production excavators, so the card opens on the biggest fleet. */
export const pickDefaultPlantType = (plantTypes: PlantTypeDTO[]): PlantTypeDTO | null =>
  plantTypes.reduce<PlantTypeDTO | null>(
    (best, candidate) =>
      best === null || (candidate.activePlantCount ?? 0) > (best.activePlantCount ?? 0) ? candidate : best,
    null
  );

export const hourlyMetricsReferenceMillis = (dataUpdatedAt: number) =>
  dataUpdatedAt > 0 ? dataUpdatedAt : DateTime.now().toMillis();

export type HourlyMetricKey = keyof HourlyMetricsChartPoint;

export type HourlyMetricSeries = {
  key: HourlyMetricKey;
  label: string;
  colour: string;
  unit: "bcm" | "bcmPerHour" | "percent";
};

export const HOURLY_METRIC_SERIES: HourlyMetricSeries[] = [
  { key: "bcmTotal", get label() { return i18n.t("hourly.bcmTotal", { ns: "dashboard" }); }, colour: "#2196F3", unit: "bcm" },
  { key: "bcmPerHour", get label() { return i18n.t("hourly.productivity", { ns: "dashboard" }); }, colour: "#90CAF9", unit: "bcmPerHour" },
  { key: "utilization", get label() { return i18n.t("hourly.utilization", { ns: "dashboard" }); }, colour: "#4CAF50", unit: "percent" },
  { key: "availability", get label() { return i18n.t("hourly.availability", { ns: "dashboard" }); }, colour: "#df80ef", unit: "percent" },
  { key: "availabilityForecastPerHour", get label() { return i18n.t("hourly.availabilityForecast", { ns: "dashboard" }); }, colour: "#8b00a3", unit: "percent" },
];

export const HOURLY_METRIC_COLOUR = HOURLY_METRIC_SERIES.reduce(
  (colours, series) => ({ ...colours, [series.key]: series.colour }),
  {} as Record<HourlyMetricKey, string>
);

// Note: recharts reports a dataKey as a plain string, so an unknown one is possible and returns
// undefined rather than being guessed at.
export const hourlyMetricSeries = (dataKey: string): HourlyMetricSeries | undefined =>
  HOURLY_METRIC_SERIES.find(series => series.key === dataKey);

export const formatHourlyMetric = (value: number, unit: HourlyMetricSeries["unit"]): string =>
  unit === "percent"
    ? formatPercent(value * 100, 1)
    : unit === "bcmPerHour"
      ? i18n.t("hourly.bcmPerHourValue", { ns: "dashboard", value: formatNumber(Math.round(value)) })
      : formatNumber(Math.round(value));

export interface HourlyMetricDetailRow {
  key: HourlyMetricKey;
  label: string;
  colour: string;
  value: string;
}

/**
 * The readings of one hour, for the panel that pins an hour the user clicked. Hours past the live
 * edge have no actuals yet, and those metrics are left out rather than shown as zero.
 */
export const buildHourDetailRows = (point: HourlyMetricsChartPoint | null): HourlyMetricDetailRow[] => {
  if (!point) return [];

  return HOURLY_METRIC_SERIES.flatMap(series => {
    const value = point[series.key];
    if (typeof value !== "number") return [];

    return [{ key: series.key, label: series.label, colour: series.colour, value: formatHourlyMetric(value, series.unit) }];
  });
};

/**
 * Clicking the pinned hour again unpins it; a click that missed the plot area changes nothing.
 * Recharts reports the clicked index as a number or, for some charts, a string.
 */
export const togglePinnedHourIndex = (pinned: number | null, clicked?: number | string | null): number | null => {
  const index = typeof clicked === "number" ? clicked : clicked ? Number(clicked) : null;
  if (index == null || !Number.isInteger(index)) return pinned;

  return index === pinned ? null : index;
};

export const HOURLY_METRIC_TARGETS: Partial<Record<HourlyMetricKey, LinkTarget>> = {
  bcmTotal: PRODUCTION_LIST_TARGET,
  bcmPerHour: PRODUCTION_LIST_TARGET,
  utilization: OPERATIONS_TARGET,
  availability: STOPPAGES_TARGET,
};

export const hourBarOutline = (
  index: number,
  { pinnedIndex, liveEdgeIndex, showLive, justUpdated }: { pinnedIndex: number | null; liveEdgeIndex: number; showLive: boolean; justUpdated: boolean }
): { stroke: string | undefined; strokeWidth: number } => {
  if (index === pinnedIndex) return { stroke: "var(--theme-focus)", strokeWidth: 2 };
  if (showLive && justUpdated && index === liveEdgeIndex) return { stroke: "var(--theme-success)", strokeWidth: 2 };
  return { stroke: undefined, strokeWidth: 0 };
};
